import type { Fnode } from "@protontech/fathom";
import { out, rule, score, type } from "@protontech/fathom";

import { FormType } from "@protontech/autofill/types";
import { flagAsProcessed, getCachedPredictionScore, getParentFormPrediction, isPredictedType, setCachedPredictionScore } from "./flags";

export const TOLERANCE_LEVEL = 0.5;

export const boolInt = (val: boolean): number => Number(val);
export const safeInt = (val: number, fallback = 0): number => (Number.isFinite(val) ? val : fallback);

const throughEffect = (effect: (fnode: Fnode) => void) => (fnode: Fnode) => {
  effect(fnode);
  return fnode;
};

/** Creates an effect that flags elements with their detection result.
 * This effect also marks elements as processed, which is crucial for
 * handling cached results in subsequent pipeline stages. */
export const typeEffect = (type: string) =>
  throughEffect((fnode) => {
    flagAsProcessed(fnode.element);

    /** Only set the cached prediction score if there's no existing note.
     * Prediction notes are typically present for cached prediction results
     * (see `outRuleWithCache`). This check prevents overwriting cached scores. */
    if (!fnode.hasNoteFor(`${type}-prediction`)) {
      setCachedPredictionScore(fnode.element, type, getTypeScore(fnode, type));
    }
  });

export const processFormEffect = throughEffect((fnode) => flagAsProcessed(fnode.element));

export const processFieldEffect = throughEffect((fnode) => {
  const { visible, type } = fnode.noteFor<{ visible: boolean | undefined; type: string | undefined }>("field")!;
  if (visible || type === "hidden") flagAsProcessed(fnode.element);
});

type ClassNote = "form" | "new-password-field" | "password-field" | "username-field" | "username-hidden-field" | "email-field" | "otp-field";

export const featureScore = (noteFor: ClassNote, featName: string) =>
  score((fnode: Fnode) => {
    // FIXME: these should be `numbers` as per fathom internals
    // but decision trees support categorical features..
    const note = fnode.noteFor<Record<string, unknown>>(noteFor)!;
    return Number(note[featName]);
  });

/** Given a field fnode: resolve the parent form fnode it belongs to.
 * Base case scenario, it's contained in a form that's currently being
 * processed in the current pipeline and available by `getting` the form
 * type on the current bound ruleset. If we're dealing with a pre-detected
 * parent: get the underlying fnode (worst case scenario, we'll re-create
 * the form feature vector in `abstract.field.ts`). */
export const getParentFormFnode = (fieldFnode: Fnode): Fnode | null => {
  const field = fieldFnode.element;
  const ruleset = fieldFnode._ruleset;
  const parentForms = ruleset.get(type("form"));

  const form = parentForms.find(({ element }) => element.contains(field));
  if (form) return form;

  const preDetectedForm = getParentFormPrediction(field);
  if (preDetectedForm) return ruleset.get(preDetectedForm);

  return null;
};

/** Checks if a given Fnode is of a specific `type`
 * by checking its score is over the tolerance level */
export const typeScoreToleranceTest = (type: string) => (fnode: Fnode) => fnode.scoreFor(type) > TOLERANCE_LEVEL;

/** Retrieves the type score for a given node and type. Ensures compatibility
 * with both pre-detected forms and classified nodes. For pre-detected forms that
 * bypass classification (and thus lack a score), it checks for the presence of
 * a type-specific note. For all other cases, it uses the score for the given type. */
export const getTypeScore = (node: Fnode | null, type: string): number => {
  if (!node) return 0;
  if (node.hasNoteFor(`${type}-prediction`)) return node.noteFor(`${type}-prediction`) ?? 0;
  return node.scoreFor(type);
};

/** These utilities allow us to by-pass the classification pipeline and
 * directly output the result from a previous detection run (consumers
 * of protonpass-fathom must flag results accordingly for this optimisation
 * to work correctly */
export const outRuleWithCache = (candidateType: string, predictionType: string, typeScoreTest: (predictionType: string) => (fnode: Fnode) => boolean = typeScoreToleranceTest) => [
  rule(type(candidateType).when(isPredictedType(predictionType)), type(`${predictionType}-prediction`).note(getCachedPredictionScore(predictionType)), {}),
  rule(type(predictionType).when(typeScoreTest(predictionType)), type(`${predictionType}-prediction`), {}),
  rule(type(`${predictionType}-prediction`), out(predictionType).through(typeEffect(predictionType)), {}),
];

export const withFnodeEl = (fn: (el: HTMLElement) => boolean) => (fnode: Fnode) => fn(fnode.element);

export type FormClassification = {
  login: boolean;
  register: boolean;
  passwordChange: boolean;
  recovery: boolean;
  noop: boolean;
};

export const getFormClassification = (formFnode: Fnode | null): FormClassification => {
  const login = getTypeScore(formFnode, FormType.LOGIN) > 0.5;
  const register = getTypeScore(formFnode, FormType.REGISTER) > 0.5;
  const passwordChange = getTypeScore(formFnode, FormType.PASSWORD_CHANGE) > 0.5;
  const recovery = getTypeScore(formFnode, FormType.RECOVERY) > 0.5;
  const detectionResults = [login, register, passwordChange, recovery];
  const noop = detectionResults.every((detected) => !detected);

  return { login, register, passwordChange, recovery, noop };
};

export const isNoopForm = (formFnode: Fnode): boolean => getFormClassification(formFnode).noop;
